/* eslint-disable no-underscore-dangle */
/**
 * `/portal/operator/trip/:bookingId` — one of the operator's trips on its
 * own screen, in English, read-only apart from the operator's own actions.
 *
 * Opened from a row's Details on Assignments, which hands the row over in
 * router state so the screen paints at once. Either way the trip is then
 * read ONCE from `/portal/operator/assignments` — its own Budapest day when
 * the row is known, else a window around today (a deep link or a pasted
 * URL) — so a reload never shows a stale driver or step. A trip that is no
 * longer the operator's is not shown at all.
 *
 * Everything on it is the driver portal's own components with English copy
 * (the `copy` props; the words live in operatorFormat.js): the route map with
 * the programme's stops, the route rail, the customer (the phone only inside
 * the API's 24-hour window, else when it appears), the dispatcher note (the
 * operator's own slot's `operator_comments`), the passenger manifest, the
 * overtime, and the receipts (RecordExpense / ExpenseList, tied to this
 * booking). Assign and Acknowledge are the Assignments table's controls
 * (TripActions.jsx). There are no driver-step buttons.
 *
 * Money is the operator's own: its fee, the cash its driver collects and the
 * balance, from `trip.money`. The payload carries no customer price, profit,
 * booking `comments` or admin comments, and nothing here reads such a field
 * — the screen renders named fields only, never the trip object whole.
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import useBackTo, { isPlainClick } from '../../../Hooks/useBackTo';
import { fetchOperatorAssignments } from '../../../Services/OperatorPortalApi';
import { operatorAssignSource } from '../assignSources';
import { fetchPortalExpenses } from '../../../Services/PortalApi';
import { formatDate, formatTime } from '../../../Utils/timezone';
import {
  budapestDayWindow, isNoShow, passengerSummary, progressIndex,
} from '../portalFormat';
import PortalRouteMap from '../Components/PortalRouteMap';
import ProgressStrip from '../Components/ProgressStrip';
import OvertimeCard from '../Components/OvertimeCard';
import RecordExpense from '../Components/RecordExpense';
import ExpenseList from '../Components/ExpenseList';
import Toast from '../Components/Toast';
import { ChevronLeftIcon } from '../Components/Icons';
import {
  CustomerCard, Facts, Manifest, Note, RouteOrProgramme,
} from '../Components/TripCards';
import AssignSheet from './AssignSheet';
import Pill from './Pill';
import {
  AcknowledgeButton, assignedNotice, DriverButton, useAcknowledge,
} from './TripActions';
import {
  ENDPOINT_LABELS_EN,
  EXPENSE_COPY_EN,
  formatAmountView,
  formatMoney,
  longDay,
  OVERTIME_COPY_EN,
  operatorLoadErrorMessage,
  paxLabel,
  PASSENGER_WORDS_EN,
  PROGRESS_STEPS_EN,
  roleLabel,
  ROUTE_MAP_COPY_EN,
  routeLabel,
  TRIP_CARD_COPY_EN,
  tripStatus,
} from './operatorFormat';

// A deep link has no row to go by, so the trip is looked for in the widest
// window the assignments endpoint takes (62 days): the past month — "This
// month" can open a trip four weeks back — and the month ahead.
const LOOKBACK_DAYS = 31;
const LOOKAHEAD_DAYS = 30;

const NOT_FOUND = 'This trip is not among your assignments. '
  + 'It may have been reassigned or cancelled.';

const findIn = async (bookingId, range) => {
  const { trips } = await fetchOperatorAssignments(range);
  return trips.find((row) => row._id === bookingId) || null;
};

/**
 * The trip from one assignments read: its own Budapest day when the row is
 * known, else the window around today. Only a known row that has moved off
 * its day costs a second read, of the window. Null when it is not the
 * operator's.
 */
const findTrip = async (bookingId, known) => {
  const day = known?.date_datetime ? formatDate(known.date_datetime) : '';
  const found = day ? await findIn(bookingId, { from: day, to: day }) : null;
  return found || findIn(bookingId, budapestDayWindow(LOOKBACK_DAYS, LOOKAHEAD_DAYS));
};

/** Progress is the lead driver's: a guide's or a coordinator's slot has none to show. */
const showsProgress = (trip) => trip.status !== 'Cancelled' && !isNoShow(trip)
  && (!trip.role || trip.role === 'driver');

function MoneyCard({ trip }) {
  const { money } = trip;
  if (!money) return null;
  const cancelled = trip.status === 'Cancelled';
  const fee = formatMoney(money.fee, money.currency);
  const adjustment = money.adjustments
    ? ` (incl. adjustment ${formatAmountView(money.adjustments)})`
    : '';

  return (
    <Facts
      title="Your money"
      rows={[
        { key: cancelled ? 'Cancellation fee' : 'Your fee', value: `${fee}${adjustment}` },
        { key: 'Cash to collect', value: formatAmountView(money.cash_to_collect) },
        { key: 'Balance', value: formatMoney(money.balance, money.currency) },
      ]}
    />
  );
}

function DriverCard({ trip, onAssign }) {
  return (
    <div className="pp-card pp-op-trip__crew">
      <div className="pp-eyebrow">
        {trip.role && trip.role !== 'driver'
          ? `${roleLabel(trip.role)} · driver & vehicle`
          : 'Driver & vehicle'}
      </div>
      <div className="pp-op-trip__crew-body">
        <DriverButton trip={trip} onAssign={onAssign} />
      </div>
    </div>
  );
}

function Receipts({
  trip, expenses, error, onSaved,
}) {
  return (
    <section className="pp-op-trip__receipts" aria-label="Expenses">
      <RecordExpense
        bookingId={trip._id}
        tripLabel={trip.booking_reference}
        onSaved={onSaved}
        copy={EXPENSE_COPY_EN}
      />
      <ExpenseList expenses={expenses} error={error} copy={EXPENSE_COPY_EN} />
      {!error && !expenses.length ? (
        <div className="pp-stub-note">No expenses recorded for this trip yet.</div>
      ) : null}
    </section>
  );
}

export default function OperatorTripDetails() {
  const { bookingId } = useParams();
  const location = useLocation();
  const handed = location.state?.trip?._id === bookingId ? location.state.trip : null;
  /**
   * Back to Assignments as the LIST was left — the period that was chosen
   * and the row that was being read. Both of those live in the history
   * entry behind this screen (`?range=` in its URL, its scroll in
   * ScrollMemory), so this has to POP, not navigate to `/portal/operator`
   * afresh: doing the latter is what reset the tab to Today and dumped the
   * operator at the top of the table. It stays a real link, so the
   * destination is visible and cmd-click still opens it in a new tab.
   */
  const backToAssignments = useBackTo('/portal/operator');
  const onBack = useCallback((event) => {
    if (!isPlainClick(event)) return;
    event.preventDefault();
    backToAssignments();
  }, [backToAssignments]);
  const [trip, setTrip] = useState(handed);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [notice, setNotice] = useState('');
  const [expenses, setExpenses] = useState([]);
  const [expensesError, setExpensesError] = useState('');

  // The row as handed over, for the read below — a ref, not a dependency:
  // the read happens once per trip, not again whenever state changes.
  const known = useRef(handed);
  known.current = handed;

  useEffect(() => {
    let cancelled = false;
    setTrip(known.current);
    setLoading(true);
    setError('');
    setActionError('');
    findTrip(bookingId, known.current)
      .then((found) => {
        if (cancelled) return;
        setTrip(found);
        if (!found) setError(NOT_FOUND);
      })
      .catch((failure) => {
        // Keep a handed-over row on screen; say it could not be refreshed.
        if (!cancelled) setError(operatorLoadErrorMessage(failure));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [bookingId]);

  useEffect(() => {
    let cancelled = false;
    setExpensesError('');
    fetchPortalExpenses({ bookingId })
      // The API narrows to this booking; the filter keeps the screen right
      // against an API that does not know the parameter yet.
      .then((rows) => {
        if (!cancelled) setExpenses((rows || []).filter((row) => row.booking_id === bookingId));
      })
      .catch((failure) => {
        if (!cancelled) setExpensesError(operatorLoadErrorMessage(failure));
      });
    return () => { cancelled = true; };
  }, [bookingId]);

  const clearNotice = useCallback(() => setNotice(''), []);

  const expenseSaved = useCallback((expense) => {
    if (!expense?._id) return;
    setExpenses((current) => [expense, ...current.filter((row) => row._id !== expense._id)]);
  }, []);

  const acknowledged = useCallback((acked, flag) => {
    setTrip((current) => (current ? { ...current, acknowledged: flag } : current));
    setNotice(`${acked.booking_reference} acknowledged`);
  }, []);
  const { acknowledge, acknowledging } = useAcknowledge({
    onAcknowledged: acknowledged,
    onError: setActionError,
  });

  const assigned = useCallback((updated) => {
    setAssigning(false);
    setNotice(assignedNotice(trip));
    // The API answers with the trip as it now stands.
    if (updated?._id === bookingId) setTrip((current) => ({ ...current, ...updated }));
  }, [bookingId, trip]);

  if (!trip) {
    return (
      <div className="pp-op-page pp-op-trip">
        <Link className="pp-op-back" to="/portal/operator" onClick={onBack}>
          <ChevronLeftIcon size={18} width={2.5} />
          Assignments
        </Link>
        {loading ? <div className="pp-state" role="status">Loading the trip…</div> : null}
        {!loading ? <div className="pp-op-error" role="alert">{error || NOT_FOUND}</div> : null}
      </div>
    );
  }

  const status = tripStatus(trip);
  const when = `${longDay(trip.date_datetime)} · ${formatTime(trip.date_datetime)}`;

  return (
    <div className="pp-op-page pp-op-trip">
      <Link className="pp-op-back" to="/portal/operator" onClick={onBack}>
        <ChevronLeftIcon size={18} width={2.5} />
        Assignments
      </Link>

      <div className="pp-op-page__head">
        <div>
          <h1 className="pp-op-title">{trip.booking_reference}</h1>
          <div className="pp-op-subtitle">{`${when} · ${routeLabel(trip)}`}</div>
        </div>
        <div className="pp-op-page__tools">
          <Pill tone={status.tone}>{status.label}</Pill>
          <AcknowledgeButton
            trip={trip}
            busy={acknowledging === trip._id}
            onAcknowledge={acknowledge}
          />
        </div>
      </div>

      {error ? <div className="pp-op-error" role="alert">{error}</div> : null}
      {actionError ? <div className="pp-op-error" role="alert">{actionError}</div> : null}

      {showsProgress(trip) ? (
        <div className="pp-card pp-op-trip__progress">
          <ProgressStrip
            index={progressIndex(trip)}
            steps={PROGRESS_STEPS_EN}
            label="Trip progress"
          />
        </div>
      ) : null}

      <div className="pp-op-trip__grid">
        <div className="pp-stack pp-op-trip__main">
          <PortalRouteMap
            trip={trip}
            target={{ point: trip.from, label: ENDPOINT_LABELS_EN.from }}
            copy={ROUTE_MAP_COPY_EN}
          />

          <RouteOrProgramme trip={trip} copy={TRIP_CARD_COPY_EN} />

          <Facts
            rows={[
              {
                key: 'Passengers',
                value: passengerSummary(trip, PASSENGER_WORDS_EN) || paxLabel(trip.passengers),
              },
              { key: 'Category', value: trip.category },
              { key: 'Flight', value: trip.flight_number },
              { key: 'Event', value: trip.event_name },
              {
                key: 'Your role',
                value: trip.role && trip.role !== 'driver' ? roleLabel(trip.role) : '',
              },
            ]}
          />

          <Manifest trip={trip} copy={TRIP_CARD_COPY_EN} />

          <OvertimeCard trip={trip} copy={OVERTIME_COPY_EN} />
        </div>

        <div className="pp-stack pp-op-trip__side">
          <DriverCard trip={trip} onAssign={() => setAssigning(true)} />

          <MoneyCard trip={trip} />

          <CustomerCard trip={trip} copy={TRIP_CARD_COPY_EN} />

          <Note label="Dispatcher note" body={trip.operator_comments} />

          <Receipts
            trip={trip}
            expenses={expenses}
            error={expensesError}
            onSaved={expenseSaved}
          />
        </div>
      </div>

      {assigning ? (
        <AssignSheet
          trip={trip}
          source={operatorAssignSource}
          onClose={() => setAssigning(false)}
          onAssigned={assigned}
        />
      ) : null}

      <Toast message={notice} onDone={clearNotice} />
    </div>
  );
}
